import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';

import Tooltip from '../../../../form/fields/common/Tooltip';
import { StyledTr, StyledTd, GroupTd, QuantityInput, TermSelect } from '../css';
import {
  COLUMN_KEYS,
  computeRowPricing,
  isRowComplete,
  getFormattedPricing,
  getSelectedTermPrice
} from '../util';

const getChildName = device =>
  _.get(device, 'fields.deviceName') ||
  _.get(device, 'fields.childName') ||
  _.get(device, 'fields.name', '');

/**
 * Renders a single child device as an interactive, priced row (mirrors the rate
 * plan sub-row): a term dropdown, the device name (+ tooltip), a Units input,
 * the selected term's unit price and the row total. Each completed device flows
 * to the order via `onRowComplete`. The leading group cell is rendered only on
 * the first device and spans all sibling device rows. Saved selections seed
 * the term and units when the popup reopens.
 */
const IotTableChildDeviceRow = ({
  device,
  columns,
  interactive,
  selectTermPlaceholder,
  rowKey,
  isFirst,
  deviceCount,
  groupLabel,
  parentToolTip,
  rateplanType,
  savedSelection,
  onRowComplete
}) => {
  const deviceName = getChildName(device);
  const toolTip = _.get(device, 'fields.toolTip', null);
  const deviceTerms = _.get(device, 'fields.terms', []);
  const hasTermData = Array.isArray(deviceTerms) && deviceTerms.length > 0;

  const [term, setTerm] = useState(_.get(savedSelection, 'term', ''));
  const [quantity, setQuantity] = useState(() =>
    String(_.get(savedSelection, 'quantity', 0))
  );

  const { monthly, oneTime } = computeRowPricing(device, { term, quantity });
  const rowTotal = monthly + oneTime;

  const isCompletedRef = useRef(false);

  useEffect(() => {
    if (!interactive || !onRowComplete) return;

    // A term-bearing child device requires an explicit term selection (no
    // auto-select) before it is priced, so the default price stays 0.
    const complete = hasTermData
      ? Boolean(term) && Number(quantity) > 0
      : isRowComplete(device, { term, quantity }, columns);

    if (complete) {
      onRowComplete(rowKey, {
        key: rowKey,
        label: deviceName,
        rateplanType,
        term,
        quantity: Number(quantity) || 0,
        monthly,
        oneTime
      });
      isCompletedRef.current = true;
    } else if (isCompletedRef.current) {
      onRowComplete(rowKey, null);
      isCompletedRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [term, quantity]);

  const renderCell = descriptor => {
    const { key, index } = descriptor;
    const cellKey = `${rowKey}-cell-${index}`;

    if (key === COLUMN_KEYS.GROUPED) {
      if (!isFirst) return null;
      return (
        <GroupTd key={cellKey} rowSpan={deviceCount}>
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px'
            }}
          >
            {groupLabel}
            {parentToolTip ? <Tooltip>{parentToolTip}</Tooltip> : null}
          </span>
        </GroupTd>
      );
    }

    if (key === COLUMN_KEYS.NAME) {
      return (
        <StyledTd key={cellKey}>
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px'
            }}
          >
            {deviceName}
            {toolTip ? <Tooltip>{toolTip}</Tooltip> : null}
          </span>
        </StyledTd>
      );
    }

    if (key === COLUMN_KEYS.RATE_PLAN) {
      if (hasTermData) {
        return (
          <StyledTd key={cellKey}>
            {interactive ? (
              <TermSelect
                name={`${rowKey}-term`}
                value={term}
                onChange={event => setTerm(event.target.value)}
              >
                <option value="">{selectTermPlaceholder}</option>
                {deviceTerms.map(termItem => {
                  const termName = _.get(termItem, 'fields.termName', '');
                  return (
                    <option
                      key={_.get(termItem, 'sys.id', termName)}
                      value={termName}
                    >
                      {termName}
                    </option>
                  );
                })}
              </TermSelect>
            ) : (
              term
            )}
          </StyledTd>
        );
      }
      return <StyledTd key={cellKey} />;
    }

    if (key === COLUMN_KEYS.UNITS) {
      if (interactive && hasTermData) {
        return (
          <StyledTd key={cellKey}>
            <QuantityInput
              type="text"
              inputMode="numeric"
              aria-label="Units"
              value={quantity}
              onChange={event => {
                const digits = event.target.value.replace(/\D/g, '');
                const cleaned = digits.replace(/^0+(?=\d)/, '');
                setQuantity(cleaned === '' ? '0' : cleaned);
              }}
            />
          </StyledTd>
        );
      }
      return (
        <StyledTd key={cellKey}>
          {Number(quantity) > 0 ? quantity : ''}
        </StyledTd>
      );
    }

    if (key === COLUMN_KEYS.TOTAL) {
      return <StyledTd key={cellKey}>{getFormattedPricing(rowTotal)}</StyledTd>;
    }

    if (key === COLUMN_KEYS.UNIT_PRICE) {
      return (
        <StyledTd key={cellKey}>
          {getFormattedPricing(
            term ? getSelectedTermPrice(deviceTerms, term) : 0
          )}
        </StyledTd>
      );
    }

    return <StyledTd key={cellKey} />;
  };

  return <StyledTr>{columns.map(renderCell)}</StyledTr>;
};

IotTableChildDeviceRow.propTypes = {
  device: PropTypes.shape({}).isRequired,
  columns: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  interactive: PropTypes.bool,
  selectTermPlaceholder: PropTypes.string,
  rowKey: PropTypes.string.isRequired,
  isFirst: PropTypes.bool,
  deviceCount: PropTypes.number,
  groupLabel: PropTypes.string,
  parentToolTip: PropTypes.string,
  rateplanType: PropTypes.string,
  savedSelection: PropTypes.shape({
    term: PropTypes.string,
    quantity: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
  }),
  onRowComplete: PropTypes.func
};

IotTableChildDeviceRow.defaultProps = {
  interactive: false,
  selectTermPlaceholder: '',
  isFirst: false,
  deviceCount: 1,
  groupLabel: '',
  parentToolTip: '',
  rateplanType: '',
  savedSelection: null,
  onRowComplete: null
};

/**
 * Renders one interactive priced row per child device when a parent entry has
 * `hasChildDevices: true`. The parent's `rateplanType` is shown as a group cell
 * spanning the device rows.
 */
const IotTableChildRow = ({
  childDevices,
  columns,
  interactive,
  selectTermPlaceholder,
  parentRowKey,
  groupLabel,
  parentToolTip,
  rateplanType,
  savedSelections,
  onRowComplete
}) => {
  if (!Array.isArray(childDevices) || childDevices.length === 0) return null;

  return (
    <>
      {childDevices.map((device, deviceIndex) => {
        const rowKey =
          _.get(device, 'sys.id') || `${parentRowKey}-device-${deviceIndex}`;

        return (
          <IotTableChildDeviceRow
            key={rowKey}
            device={device}
            columns={columns}
            interactive={interactive}
            selectTermPlaceholder={selectTermPlaceholder}
            rowKey={rowKey}
            isFirst={deviceIndex === 0}
            deviceCount={childDevices.length}
            groupLabel={groupLabel}
            parentToolTip={parentToolTip}
            rateplanType={rateplanType}
            savedSelection={savedSelections[rowKey]}
            onRowComplete={onRowComplete}
          />
        );
      })}
    </>
  );
};

IotTableChildRow.propTypes = {
  childDevices: PropTypes.arrayOf(PropTypes.shape({})),
  columns: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  interactive: PropTypes.bool,
  selectTermPlaceholder: PropTypes.string,
  parentRowKey: PropTypes.string,
  groupLabel: PropTypes.string,
  parentToolTip: PropTypes.string,
  rateplanType: PropTypes.string,
  savedSelections: PropTypes.shape({}),
  onRowComplete: PropTypes.func
};

IotTableChildRow.defaultProps = {
  childDevices: [],
  interactive: false,
  selectTermPlaceholder: '',
  parentRowKey: '',
  groupLabel: '',
  parentToolTip: '',
  rateplanType: '',
  savedSelections: {},
  onRowComplete: null
};

export default IotTableChildRow;
